import type { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import type { RootState } from "../../../root/store/state";
import { Contact, ContactState } from "../../../common/model/crm/contact";

const getDefaultState = () => {
  return {
    Contact: null,
    EmailConfigurationId: 0,
    IsInitialized: false,
    ClientGroupIds: null,
    isBillingContact: false,
    hasAllTicketsTag: false,
    hasAllInvoicesTag: false,
    hasAllAssetsTag: false,
  };
};

const state: ContactState = {
  Contact: null as unknown as Contact,
  EmailConfigurationId: 0,
  IsInitialized: false,
  ClientGroupIds: null,
  isBillingContact: false as boolean,
  hasAllTicketsTag: false as boolean,
  hasAllInvoicesTag: false as boolean,
  hasAllAssetsTag: false as boolean,
};

const getters: GetterTree<ContactState, RootState> = {
  getContactId(state): number {
    if (state.Contact != null) return state.Contact.contactId;
    else return 0;
  },

  getContactName(state): string {
    if (state.Contact != null)
      return state.Contact.firstName + " " + state.Contact.lastName;
    else return "";
  },

  getContactEmail(state): String {
    return state.Contact ? state.Contact.emailAddress : "";
  },

  getClientId(state): number | null {
    if (state.Contact == null) {
      return null;
    }
    return state.Contact.clientId ?? null;
  },

  getServiceProviderId(state): number | undefined {
    if (state.Contact != null) return state.Contact.serviceProviderId;
    else return 0;
  },

  getIsBillingContact(state): boolean | undefined {
    return state.isBillingContact;
  },

  getContact(state): Contact {
    return state.Contact;
  },

  getEmailConfigutaionId(state): number | null {
    return state.EmailConfigurationId;
  },

  getClientGroupIds(state): number[] | null {
    return state.ClientGroupIds;
  },

  getIsStateInitialized(state): boolean {
    return state.IsInitialized;
  },

  getHasAllTicketsTag(state): boolean {
    return state.hasAllTicketsTag;
  },

  getHasAllInvoicesTag(state): boolean {
    return state.hasAllInvoicesTag;
  },

  getHasAllAssetsTag(state): boolean {
    return state.hasAllAssetsTag;
  },

  /** Read from the contact fetched at sign-in; the AssetAPI still decides every call from CRM itself. */
  getRemoteAccessLevel(state): number {
    return state.Contact?.remoteAccessLevel ?? 0;
  },
};

const mutations: MutationTree<ContactState> = {
  async SET_CONTACT(state, contact) {
    state.Contact = contact;

    const { default: contactService } = await import(
      "../../../common/services/contact-service"
    );
    await contactService.setContactTagPermissions();
    state.IsInitialized = true;
  },

  SET_EmailConfigurationId(state, EmailConfigurationId) {
    state.EmailConfigurationId = EmailConfigurationId;
  },

  SET_ClientGroupIds(state, ClientGroupIds) {
    state.ClientGroupIds = ClientGroupIds;
  },

  RESET_STATE(state) {
    Object.assign(state, getDefaultState());
  },

  SET_IsBillingContact(state, isBillingContact) {
    state.isBillingContact = isBillingContact;
  },

  SET_HasAllTicketsTag(state, hasAllTicketsTag) {
    state.hasAllTicketsTag = hasAllTicketsTag;
  },

  SET_HasAllInvoicesTag(state, hasAllInvoicesTag) {
    state.hasAllInvoicesTag = hasAllInvoicesTag;
  },

  SET_HasAllAssetsTag(state, hasAllAssetsTag) {
    state.hasAllAssetsTag = hasAllAssetsTag;
  },
};

const actions: ActionTree<ContactState, RootState> = {};

const namespaced: boolean = true;

export const ContactModule: Module<ContactState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
